<template>
  <!-- 哨兵安全监控 · 顶栏（桌面端 + 手机端统一视觉） -->
  <header class="header-component">
    <div class="header-content">
      <!-- 左侧：折叠按钮 + 品牌 -->
      <div class="header-left">
        <div class="menu-toggle" @click="toggleMenu">
          <i :class="isDesktop ? 'el-icon-s-fold' : 'el-icon-menu'"></i>
        </div>
        <div class="logo">
          <div class="logo-container">
            <!-- 哨兵品牌图标（盾牌 + 雷达波纹） -->
            <svg class="logo-icon" viewBox="0 0 48 48" width="34" height="34" aria-hidden="true">
              <defs>
                <linearGradient id="sentinel-logo-grad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stop-color="#4080FF"/>
                  <stop offset="1" stop-color="#0E42D2"/>
                </linearGradient>
              </defs>
              <path d="M24 2.5 L43 9.5 V22 C43 33.2 34.8 41.6 24 45.5 C13.2 41.6 5 33.2 5 22 V9.5 Z" fill="url(#sentinel-logo-grad)"/>
              <circle cx="24" cy="22" r="9.5" fill="none" stroke="#fff" stroke-width="2.6" opacity="0.92"/>
              <circle cx="24" cy="22" r="3.6" fill="#fff"/>
              <path d="M24 22 L33.5 31.5" stroke="#fff" stroke-width="2.8" stroke-linecap="round"/>
            </svg>
            <div class="logo-text">
              <span class="logo-chinese">哨兵安全监控</span>
              <span class="logo-english">SENTINEL SECURITY</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 中间：当前页面标题（仅桌面端） -->
      <div class="header-middle">
        <span class="page-title" v-if="pageTitle">{{ pageTitle }}</span>
      </div>

      <!-- 右侧：通知 / 消息 / 用户 -->
      <div class="header-right">
        <div class="header-icon-item" v-if="isDesktop" title="通知">
          <el-badge :value="3" class="item">
            <i class="el-icon-bell"></i>
          </el-badge>
        </div>

        <div class="header-divider" v-if="isDesktop"></div>

        <div class="header-icon-item" v-if="isDesktop" title="消息">
          <el-badge :value="5" class="item">
            <i class="el-icon-message"></i>
          </el-badge>
        </div>

        <div class="header-divider" v-if="isDesktop"></div>

        <el-dropdown trigger="click">
          <div class="user-info">
            <el-avatar size="small" :src="userAvatar">
              <i class="el-icon-user-solid"></i>
            </el-avatar>
            <span class="user-name" v-if="isDesktop">{{ userName }}</span>
            <i class="el-icon-arrow-down"></i>
          </div>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item @click.native="handlePersonalCenter">
              <i class="el-icon-user"></i>
              <span>个人中心</span>
            </el-dropdown-item>
            <el-dropdown-item @click.native="handleSettings">
              <i class="el-icon-setting"></i>
              <span>设置</span>
            </el-dropdown-item>
            <el-dropdown-item divided @click.native="handleLogout">
              <i class="el-icon-switch-button"></i>
              <span>退出登录</span>
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </div>
  </header>
</template>

<script>
export default {
  name: 'HeaderComponent',
  props: {
    isDesktop: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    // 从Vuex store中获取用户名
    userName() {
      return this.$store.getters.getUserName || '管理员'
    },
    // 从Vuex store中获取用户头像
    userAvatar() {
      return this.$store.getters.getUserAvatar || ''
    },
    // 当前页面标题（从菜单列表匹配）
    pageTitle() {
      const menu = this.$store.state.menuList.find(m => m.path === this.$route.path)
      return menu ? menu.name : ''
    }
  },
  methods: {
    toggleMenu() {
      this.$emit('toggle-menu')
    },
    // 处理个人中心点击事件
    handlePersonalCenter() {
      this.$router.push('/mine')
    },
    // 处理设置点击事件
    handleSettings() {
      // 跳转到账号管理页的"设置" tab
      this.$router.push({ path: '/mine', query: { tab: 'settings' } })
    },
    // 处理退出登录点击事件
    handleLogout() {
      this.$store.dispatch('logout')
      this.$router.push('/login')
    }
  }
}
</script>

<style scoped>
/* 顶栏基础样式 */
.header-component {
  background-color: #fff;
  color: #1D2129;
  box-shadow: 0 1px 0 rgba(16, 24, 40, 0.06), 0 2px 8px rgba(16, 24, 40, 0.04);
  height: 64px;
  display: flex;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

/* 头部内容容器 */
.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 0 20px;
}

@media (min-width: 769px) {
  .header-content {
    padding: 0 24px;
  }
}

/* 左侧部分 */
.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

/* 折叠 / 菜单按钮 */
.menu-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  font-size: 19px;
  cursor: pointer;
  color: #4E5969;
  transition: all 0.2s ease;
}

.menu-toggle:hover {
  background-color: #F2F4F8;
  color: #165DFF;
}

/* Logo */
.logo {
  display: flex;
  align-items: center;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
}

/* 品牌图标 */
.logo-icon {
  flex-shrink: 0;
  filter: drop-shadow(0 2px 4px rgba(22, 93, 255, 0.35));
}

/* Logo 文字 */
.logo-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  line-height: 1.15;
}

.logo-chinese {
  font-size: 16px;
  font-weight: 700;
  color: #1D2129;
  letter-spacing: 1px;
}

.logo-english {
  font-size: 9px;
  font-weight: 600;
  color: #86909C;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  margin-top: 3px;
}

/* 中间部分 */
.header-middle {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 0 24px;
}

.page-title {
  font-size: 15px;
  font-weight: 600;
  color: #1D2129;
  position: relative;
  padding-left: 14px;
}

.page-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 16px;
  border-radius: 2px;
  background: linear-gradient(180deg, #4080FF, #165DFF);
}

/* 右侧部分 */
.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 图标项 */
.header-icon-item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  font-size: 18px;
  color: #4E5969;
  cursor: pointer;
  transition: all 0.2s ease;
}

.header-icon-item:hover {
  background-color: #F2F4F8;
  color: #165DFF;
}

/* 分隔线 */
.header-divider {
  width: 1px;
  height: 20px;
  background-color: #E5E6EB;
}

/* 用户信息 */
.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 8px;
  cursor: pointer;
  color: #1D2129;
  font-size: 14px;
  transition: background-color 0.2s ease;
}

.user-info:hover {
  background-color: #F2F4F8;
}

.user-info i {
  font-size: 13px;
  color: #4E5969;
}

.user-info .el-avatar {
  width: 30px;
  height: 30px;
  background: linear-gradient(135deg, #4080FF, #0E42D2);
  color: #fff;
  font-size: 14px;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: #1D2129;
}

/* 响应式 */
@media (max-width: 768px) {
  .logo-english {
    display: none;
  }
  .logo-chinese {
    font-size: 15px;
  }
  .user-info {
    gap: 4px;
  }
}
</style>
